import { makeObservable, observable, computed, action, runInAction, toJS } from 'mobx';
import {
  getCommissionSummary,
  getEmployeeCommissions,
  markTransactionsAsPaid,
  getCommissionSettings,
  updateCommissionSettings,
  getTransactions
} from '../helpers/api';

class FinanceStore {
  _summary = [];
  _employeeReport = null;
  _settings = null;
  _transactions = [];
  _selectedYear = new Date().getFullYear();
  _selectedMonth = new Date().getMonth() + 1; // 1-12
  _isLoading = false;
  _error = null;
  _successMessage = null;

  constructor() {
    makeObservable(this, {
      _summary: observable,
      _employeeReport: observable,
      _settings: observable,
      _transactions: observable,
      _selectedYear: observable,
      _selectedMonth: observable,
      _isLoading: observable,
      _error: observable,
      _successMessage: observable,

      // Computed
      summary: computed,
      employeeReport: computed,
      settings: computed,
      transactions: computed,
      selectedYear: computed,
      selectedMonth: computed,
      isLoading: computed,
      error: computed,
      successMessage: computed,

      // Actions
      setPeriod: action,
      loadSummary: action,
      loadEmployeeReport: action,
      loadTransactions: action,
      markAsPaid: action,
      loadSettings: action,
      saveSettings: action,
      setError: action,
      setSuccess: action,
      clearMessages: action
    });
  }

  // ===== Getters =====

  get summary() {
    return toJS(this._summary);
  }

  get employeeReport() {
    return toJS(this._employeeReport);
  }

  get settings() {
    return toJS(this._settings);
  }

  get transactions() {
    return toJS(this._transactions);
  }

  get selectedYear() {
    return this._selectedYear;
  }

  get selectedMonth() {
    return this._selectedMonth;
  }

  get isLoading() {
    return this._isLoading;
  }

  get error() {
    return this._error;
  }

  get successMessage() {
    return this._successMessage;
  }

  // ===== Actions =====

  setPeriod(year, month) {
    this._selectedYear = year;
    this._selectedMonth = month;
  }

  async loadSummary() {
    this._isLoading = true;
    this._error = null;

    try {
      const data = await getCommissionSummary(this._selectedYear, this._selectedMonth);

      runInAction(() => {
        this._summary = data.summary || [];
        this._isLoading = false;
      });

      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao carregar resumo de comissões';
        this._isLoading = false;
      });
      console.error('Failed to load commission summary:', err);
      return false;
    }
  }

  async loadEmployeeReport(employeeId) {
    this._isLoading = true;
    this._error = null;

    try {
      const data = await getEmployeeCommissions(employeeId, this._selectedYear, this._selectedMonth);

      runInAction(() => {
        this._employeeReport = data;
        this._isLoading = false;
      });

      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao carregar comissões do funcionário';
        this._isLoading = false;
      });
      console.error('Failed to load employee commissions:', err);
      return false;
    }
  }

  async markAsPaid(transactionIds) {
    this._isLoading = true;
    this._error = null;
    this._successMessage = null;

    try {
      const data = await markTransactionsAsPaid(transactionIds);

      runInAction(() => {
        this._successMessage = data.message || 'Transações marcadas como pagas';
        this._isLoading = false;
      });

      // Reload data
      await this.loadSummary();

      if (this._employeeReport) {
        await this.loadEmployeeReport(this._employeeReport.employee.id);
      }

      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao marcar transações como pagas';
        this._isLoading = false;
      });
      console.error('Failed to mark transactions as paid:', err);
      return false;
    }
  }

  async loadSettings() {
    this._isLoading = true;
    this._error = null;

    try {
      const data = await getCommissionSettings();

      runInAction(() => {
        this._settings = data.commission || {};
        this._isLoading = false;
      });

      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao carregar configurações';
        this._isLoading = false;
      });
      console.error('Failed to load commission settings:', err);
      return false;
    }
  }

  async saveSettings(settingsData) {
    this._isLoading = true;
    this._error = null;
    this._successMessage = null;

    try {
      const data = await updateCommissionSettings(settingsData);

      runInAction(() => {
        this._settings = data.commission || {};
        this._successMessage = 'Configurações salvas com sucesso';
        this._isLoading = false;
      });

      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao salvar configurações';
        this._isLoading = false;
      });
      console.error('Failed to save commission settings:', err);
      return false;
    }
  }

  async loadTransactions() {
    this._isLoading = true;
    this._error = null;

    try {
      const data = await getTransactions(this._selectedYear, this._selectedMonth);

      runInAction(() => {
        this._transactions = data.transactions || [];
        this._isLoading = false;
      });

      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao carregar transações';
        this._isLoading = false;
      });
      console.error('Failed to load transactions:', err);
      return false;
    }
  }

  setError(message) {
    this._error = message;
  }

  setSuccess(message) {
    this._successMessage = message;
  }

  clearMessages() {
    this._error = null;
    this._successMessage = null;
  }
}

const financeStore = new FinanceStore();
export default financeStore;
